import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ReportingQuery } from '~/QueryHooks/ReportingQuery'
import Layout from '~/components/Layout'
import { useDebounce } from 'use-lodash-debounce';
import i18n from '~/components/i18n';
import { FiltersWrapper } from '../../FutureContracts/style';
import { Dropdown, Input, Popup } from 'semantic-ui-react';
import TableComponent from '~/components/TableComponent';
import { RouteTypes } from '~/routes/constants';
import { Link } from 'react-router-dom';
import CustomIcon from '~/components/CustomIcon';
import { El } from '~/components/Layout/style';
import { DownloadService } from '~/api/services/downloadFiles';
import { useAuth } from '~/routes/components/AuthProvider';
import { LIGHT_USER_ROLE_SLUG } from '~/Constants';
import ExcelExport from '~/components/ExcelExport';
import { handleErrors } from '~/utils/helpers';

const defaultFilterData = {
  rows: 20,
  page: 0,
  search: '',
  month: ''
}

const getTableColumnHeaders = () => [
  {
    name: i18n.t('State'),
    key: 'name'
  },
  {
    name: i18n.t('Department of Agriculture'),
    key: 'districtName'
  },
  {
    name: i18n.t('District no.'),
    key: 'number'
  },
  {
    name: i18n.t('Number of farmers'),
    key: 'farmersCount'
  },
  {
    name: i18n.t('Amount of milk in kg'),
    key: 'amount',
    type: 'no-unit-number',
    decimalScale: 0
  },
  {
    name: i18n.t('Actions')
  }
]

const DistrictDelivery: React.FC = () => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [isBusyPdf, setIsBusyPdf] = useState(false)
  const debouncedValue = useDebounce(search, 300);
  const [dataFiltered, setDataFiltered] = useState(defaultFilterData)
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const lightUserLogged = (roleSlug === LIGHT_USER_ROLE_SLUG)

  const actions = [
    {
      popupMessage: i18n.t('Details'),
      type: 'icon',
      name: 'eye',
      size: 'large',
      actionTpe: 'details',
      renderActions: getActions
    }
  ]

  const {
    data: districtSettings,
    refetch: fetchSettings,
    isLoading: isBusySettings
  } = ReportingQuery.getDistrictDeliverySettings()

  const {
    data: districtData,
    isLoading: isBusyData,
    isFetching: isFetchingDistricts,
    refetch: fetchDistrict
  } = ReportingQuery.getDistrictDeliveries(
    {
      ...dataFiltered,
      page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1,
      month: dataFiltered.month === '' ? districtSettings?.selectedMonth || '' : dataFiltered.month
    },
    Boolean(districtSettings))

  useEffect(() => {
    fetchSettings()
  }, [t])

  useEffect(() => {
    fetchDistrict()
  }, [dataFiltered])

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 })
  }, [debouncedValue])

  function getActions(row) {
    return row?.amount > 0 && <Popup
      key={row.id}
      content={t('Details')}
      trigger={<Link
        key={row.id + 'link'}
        to={RouteTypes.REPORTING_DISTRICT_DELIVERY_DETAILS.replace(':id', row?.id) + `?month=${dataFiltered.month}`}
      >
        <CustomIcon
          name={'eye'}
          size={'large'}
        />
      </Link>}
    />
  }

  function renderFilters() {
    return <FiltersWrapper>
      <Input
        icon={{ name: 'search', circular: true, link: true }}
        placeholder={t('Search...')}
        value={search}
        onChange={(e, data) => setSearch(data.value)}
      />
      <Dropdown
        selection
        selectOnBlur={false}
        placeholder={t('Select month')}
        value={dataFiltered.month || districtSettings?.selectedMonth}
        options={districtSettings?.months?.map(month => ({ value: month?.key, text: month?.label })) || []}
        onChange={(e, data) => setDataFiltered({ ...dataFiltered, month: data.value as string })}
      />
    </FiltersWrapper>
  }

  function renderTable() {
    return <TableComponent
      tableColumnHeaders={getTableColumnHeaders()}
      animated
      isFetchingData={isFetchingDistricts}
      actions={actions}
      removePagination
      dataFiltered={dataFiltered}
      handleChangeDataFiltered={(dataFiltered) => setDataFiltered(dataFiltered)}
      rows={districtData?.districts || []}
      total={districtData?.districts?.length || 0}
    />
  }

  async function handlePDF() {
    setIsBusyPdf(true)
    try {
      const month = dataFiltered.month || districtSettings?.selectedMonth
      const { search } = dataFiltered
      await DownloadService.downloadFile('reporting/district-deliveries/export/pdf', 'delivery_pdf', { month, search })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusyPdf(false)
  }

  function renderActions() {
    const month = dataFiltered.month || districtSettings?.selectedMonth
    const { search } = dataFiltered
    return <>
      <ExcelExport
        url={'reporting/district-deliveries/export/excel'}
        data={{ month, search }}
        fileName={'delivery_export'}
      />
      <El.PageHeaderActionButton
        loading={isBusyPdf}
        disabled={isBusyPdf}
        onClick={() => handlePDF()}
      >{t('PDF')}</El.PageHeaderActionButton>
    </>
  }

  return <Layout
    title={t('Delivery district no')}
    segmented
    isBusy={isBusyData || isBusySettings}
    filters={renderFilters()}
    actions={!lightUserLogged && renderActions()}
  >
    {renderTable()}
  </Layout>
}
export default DistrictDelivery